import { Notify } from "@flexisaf/flexibull2";

import { updateCurrentMeetingAgendaPrayerItems } from "@/features/agenda";
import { baseApi } from "@/store/base-api";
import { encodeInterpolatedUrl } from "@/utils/string-utils";
import { MeetingStatus, Methods, PrayerStatuses, Unit } from "@/utils/enums";
import { List, QueryFulfilledError } from "@/types/common";
import { Department, Faculty } from "@/features/users/users-api";
import { config } from "@/utils/config";

export const prayersApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getPrayers: builder.query<List<Prayer>, PrayerParams>({
            query: (params) => ({
                url: "/prayers",
                params,
            }),

            providesTags: ["PRAYERS"],
        }),

        getPrayerById: builder.query<Prayer, string>({
            query: (id) => ({
                url: `/prayers/${id}`,
            }),
            providesTags: ["PRAYER"],
        }),
        createPrayer: builder.mutation<any, Prayer>({
            query: (data) => ({
                url: "/prayers/prayer-creation",
                method: Methods.Post,
                data,
            }),
            invalidatesTags: ["PRAYERS"],
            async onQueryStarted(_, { queryFulfilled: qf }) {
                qf.then(() => Notify("Prayer Created Successfully")).catch(
                    (err: QueryFulfilledError) => Notify(err.error?.message, { status: "error" })
                );
            },
        }),
        updatePrayer: builder.mutation<any, any>({
            query: ({ id, ...data }) => ({
                url: `/prayers/${id}/prayer-update`,
                method: Methods.Put,
                data,
            }),
            invalidatesTags: ["PRAYER", "PRAYERS", "COMMENTS"],
            async onQueryStarted(_, { queryFulfilled: qf, dispatch }) {
                qf.then(({ data }) => {
                    updateCurrentMeetingAgendaPrayerItems(data, dispatch);
                    Notify("Prayer Updated Successfully");
                }).catch((err: QueryFulfilledError) =>
                    Notify(err.error?.message, { status: "error" })
                );
            },
        }),
        getPaperlessDepartments: builder.query<PaperlessDepartment[], { facultyId: string }>({
            query: (params) => ({
                url: `data/departments/${params.facultyId}`,
            }),
        }),
        getFaculties: builder.query<Faculty[], void>({
            query: () => ({
                url: `data/faculties`,
            }),
        }),
        detelePrayer: builder.mutation<any, any>({
            query: (id) => ({
                url: `/prayers/${id}`,
                method: Methods.Delete,
            }),
            async onQueryStarted(req, { queryFulfilled: qf }) {
                qf.then(() => Notify("Prayer Deleted Successfully")).catch(
                    (err: QueryFulfilledError) => Notify(err.error?.message, { status: "error" })
                );
            },
        }),
        getSRMSResults: builder.query<any, SRMSResultParams>({
            query: ({ subProgramId, sessionId, ...params }) => ({
                url: encodeInterpolatedUrl`/srms/student-result/${subProgramId}/${sessionId}`,
                params,
            }),
        }),
        getSRMSSessions: builder.query<any, void>({
            query: () => ({
                url: "srms/sessions",
            }),
        }),
        getSRSMSSubprograms: builder.query<any, any>({
            query: (params) => ({
                url: "srms/subprograms",
                params,
            }),
        }),
        getSRMSDepartments: builder.query<any, any>({
            query: (params) => ({
                url: "srms/departments",
                params,
            }),
        }),
        getSRMSFaculties: builder.query<any, void>({
            query: () => ({
                url: "srms/faculties",
            }),
        }),
        getPrayerReport: builder.mutation<Blob, number>({
            query: (id) => ({
                url: `reports/prayers/${id}/pdf`,
                method: Methods.Get,
                cache: "no-cache",
                responseType: "blob",
            }),
            onQueryStarted(_, { queryFulfilled: qf }) {
                qf.catch((err) =>
                    Notify((<QueryFulfilledError>err).error?.message, { status: "error" })
                );
            },
        }),
        downloadPrayerAttachments: builder.mutation<Blob, number>({
            query: (id) => ({
                url: `/documents/prayers/${id}/download-attachments`,
                method: Methods.Get,
                cache: "no-cache",
                responseType: "blob",
            }),
            onQueryStarted(_, { queryFulfilled: qf }) {
                qf.catch((err) =>
                    Notify((<QueryFulfilledError>err).error?.message, { status: "error" })
                );
            },
        }),
        downloadPrayerAttachmentsByMeetingAndDepartment: builder.mutation<
            Blob,
            { meetingId: number; departmentId: string }
        >({
            query: ({ meetingId, departmentId }) => ({
                url: `/documents/prayers/meetings/${meetingId}/departments/${departmentId}/download-attachments`,
                method: Methods.Get,
                cache: "no-cache",
                responseType: "blob",
            }),
        }),
        getOrganizationContexts: builder.query({
            queryFn: async (arg, queryApi, extraOptions) => {
                const { user } = (<any>queryApi.getState()).auth;
                try {
                    const response = await fetch(
                        `${config.apiUrl}/organizations/${user.organisation}/contexts`,
                        {
                            method: Methods.Get,
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${arg}`,
                            },
                        }
                    );

                    if (!response.ok) {
                        return {
                            error: {
                                status: response.status,
                                message: "Failed to fetch organization contexts",
                            },
                        };
                    }

                    const data = await response.json();
                    return { data };
                } catch (error) {
                    return { error: { status: 500, message: "Internal Server Error" } };
                }
            },
        }),
    }),
});

export interface LocalResult {
    id: number;
    url: string;
}

export interface Result extends LocalResult {
    level: string;
    semester: string;
    session: string;
    subprogram: string;
}

export interface PrayerParams {
    category?: string;
    departmentId?: string;
    facultyId?: string;
    meetingId?: number;
    meetingNumber?: number;
    type?: string;
    limit?: number;
    offset?: number;
    status?: string;
}

export interface Prayer {
    meetingId: number;
    category: string;
    createdAt: string;
    department?: Department;
    faculty?: Faculty;
    facultyId: string;
    departmentId: string;
    description: string;
    id: number;
    meeting: Meeting;
    organization: string;
    results: [] | Result[];
    localResults: [] | LocalResult[];
    reason?: string;
    status: PrayerStatuses;
    title: string;
    otherAttachments: string;
    roleId: string;
}

interface Meeting {
    createdAt: string;
    datetime: string;
    departmentId: string;
    description: string;
    facultyId: string;
    id: number;
    organization: string;
    otherAttachment: string;
    preAgenda: string;
    status: MeetingStatus;
    title: string;
    type: string;
    unit: Unit;
    updatedAt: string;
    venue: string;
}

export type SRMSResultParams = {
    "level-id"?: string;
    "semester-id"?: string;
    "report-type"?: string;
    context?: string;
    subProgramId: string;
    sessionId: string;
};

export enum ReportType {
    Senate = "SENATE",
    Student = "STUDENT",
    PassRepeat = "PASS_REPEAT",
    Outstanding = "OUTSTANDING",
    ExamResult = "EXAM_RESULT",
    Siwes = "SIWES",
    Pass = "PASS",
    Resit = "RESIT",
    Relegation = "RELEGATION",
    MissedSiwes = "MISSED_SIWES",
    AcademicStatus = "ACADEMIC_STATUS",
    PressRelease = "PRESS_RELEASE",
    Transcript = "TRANSCRIPT",
}
interface PaperlessDepartment {
    id: number;
    departmentId: string;
    name: string;
    abbreviation: string;
    faculty: {
        id: number;
        facultyId: string;
        name: string;
        abbreviation: string;
        organizationId: string;
        createdAt: string;
        updatedAt: string;
    };
    organizationId: string;
    createdAt: string;
    updatedAt: string;
}

export const {
    useGetPrayersQuery,
    useGetPrayerByIdQuery,
    useCreatePrayerMutation,
    useUpdatePrayerMutation,
    useGetSRMSResultsQuery,
    useGetSRMSSessionsQuery,
    useGetSRSMSSubprogramsQuery,
    useGetSRMSDepartmentsQuery,
    useGetSRMSFacultiesQuery,
    useGetPrayerReportMutation,
    useDownloadPrayerAttachmentsMutation,
    useDownloadPrayerAttachmentsByMeetingAndDepartmentMutation,
    useGetOrganizationContextsQuery,
    useGetPaperlessDepartmentsQuery,
    useGetFacultiesQuery,
} = prayersApi;
